import { createContext, useEffect, useState, type ReactNode } from "react";
import {
  type User as FirebaseUser,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { auth, functions, isDevMode } from "@/lib/firebase";
import { initFcm, initOnMessage } from "@/lib/fcm";
import { LoadingScreen } from "@/pages/LoadingScreen";

export type UserRole = "admin" | "agent" | "farmer" | "merchant" | "investor";

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  roles?: UserRole[];
  walletUsd?: number;
  totalInvestedUsd?: number;
  totalEarnedUsd?: number;
  kycStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  kycRejectionReason?: string;
  onboardingComplete?: boolean;
  primaryGoal?: 'sell' | 'credit' | 'monitor';
}

export interface AuthContextValue {
  user: FirebaseUser | null;
  userProfile: UserProfile | null;
  /** All roles attached to this account. Single-role accounts have length 1. */
  accountRoles: UserRole[];
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const DEV_MOCK_PROFILE: UserProfile = {
  uid: "dev-user-001",
  email: "alain@mombongo.cd",
  displayName: "Alain",
  role: "investor",
  walletUsd: 500,
  totalInvestedUsd: 1200,
  totalEarnedUsd: 180,
  kycStatus: "none",
};

const DEV_MOCK_USER = {
  uid: DEV_MOCK_PROFILE.uid,
  email: DEV_MOCK_PROFILE.email,
  displayName: DEV_MOCK_PROFILE.displayName,
  photoURL: null,
} as unknown as FirebaseUser;

const fetchUserProfile = httpsCallable<Record<string, never>, UserProfile>(functions, 'getUserProfile')
const createUserProfile = httpsCallable<Record<string, unknown>, UserProfile>(functions, 'createUserProfile')

export function AuthProvider({ children }: { children: ReactNode }) {
  const devMode = isDevMode() || import.meta.env.MODE === "test";
  const qc = useQueryClient();

  const [user, setUser] = useState<FirebaseUser | null>(devMode ? DEV_MOCK_USER : null);
  const [authLoading, setAuthLoading] = useState(!devMode);

  // Keep userProfile in React Query so qc.invalidateQueries({ queryKey: ['userProfile'] })
  // from WalletModals / InvestModal / PaymentModal actually triggers a re-fetch.
  const { data: queriedProfile } = useQuery<UserProfile | null>({
    queryKey: ['userProfile'],
    queryFn: async () => {
      if (devMode) return DEV_MOCK_PROFILE;
      const result = await fetchUserProfile({});
      if (result.data) return result.data;
      // Orphaned account: Firebase Auth user exists but no Firestore profile doc.
      // This happens when createUserProfile CF was unavailable at signup time.
      // Heal it now by creating a default profile (investor role; user can change later).
      if (user) {
        const healed = await createUserProfile({
          fullName: user.displayName ?? user.email ?? 'Utilisateur',
          email: user.email ?? '',
          role: 'investor',
          preferredLanguage: 'fr',
        });
        return healed.data ?? null;
      }
      return null;
    },
    enabled: devMode || !!user,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (devMode) return;
    initOnMessage();
  }, [devMode]);

  useEffect(() => {
    if (devMode) return;

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      if (firebaseUser) {
        qc.invalidateQueries({ queryKey: ['userProfile'] });
        initFcm();
      } else {
        qc.removeQueries({ queryKey: ['userProfile'] });
      }
    });

    return unsubscribe;
  }, [devMode, qc]);

  const userProfile = queriedProfile ?? (devMode ? DEV_MOCK_PROFILE : null);
  const accountRoles: UserRole[] = userProfile?.roles ?? (userProfile?.role ? [userProfile.role] : []);

  if (authLoading) return <LoadingScreen />;

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        accountRoles,
        isLoading: authLoading,
        isAuthenticated: !!user,
        signOut: async () => {
          if (devMode) {
            setUser(null);
          } else {
            await firebaseSignOut(auth);
          }
          qc.removeQueries({ queryKey: ['userProfile'] });
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
